import type { BlockField } from '@/blocks/domain/types';

export type FieldKind = 'header' | 'customer_block' | 'signature';

type FieldDefaultsForDocType = Partial<Record<FieldKind, BlockField[]>>;

let counter = 0;
function makeId(prefix: string): string {
  counter += 1;
  return `${prefix}-${counter}`;
}

function field(partial: Omit<Partial<BlockField>, 'sortOrder'> & { id?: string }): BlockField {
  return {
    id: partial.id ?? makeId('fld'),
    label: partial.label ?? '',
    value: partial.value ?? '',
    width: partial.width ?? 'full',
    align: partial.align ?? 'left',
    style: partial.style ?? 'label_value',
    visible: partial.visible ?? true,
    sortOrder: 0,
    ...(partial.name !== undefined ? { name: partial.name } : {}),
    ...(partial.sectionId !== undefined ? { sectionId: partial.sectionId } : {}),
    ...(partial.column !== undefined ? { column: partial.column } : {})
  };
}

/** Fixed id: the editor builds its draft AND its clean baseline from the same content, so a random id would read as an unsaved edit. */
export const CUSTOMER_COLUMNS_SECTION_ID = 'sec-customer-columns';

const QUOTE_DEFAULTS: FieldDefaultsForDocType = {
  header: (() => {
    const list = [
      field({ id: 'hdr-title', style: 'title', name: 'Title', value: '{{quote.name}}' }),
      field({
        id: 'hdr-quote-number',
        width: 'half',
        align: 'right',
        label: 'Quote #',
        value: '{{quote.number}}'
      }),
      field({ id: 'hdr-date-issued', width: 'half', align: 'right', label: 'Date issued', value: '{{quote.date}}' }),
      field({ id: 'hdr-valid-until', width: 'half', align: 'right', label: 'Valid until', value: '{{quote.valid_until}}' })
    ];
    return list.map((f, idx) => ({ ...f, sortOrder: idx }));
  })(),
  // LLAMS-844: a new Bill to / Job site block stores REAL columns, like every other element block: one 2-column
  // section, the Bill to group in column 0 and the Job site group in column 1. The Job site rows are bound to the
  // job site variables and named, so none renders empty and every row has its own name in the Sections tree.
  customer_block: (() => {
    const left = { sectionId: CUSTOMER_COLUMNS_SECTION_ID, column: 0 };
    const right = { sectionId: CUSTOMER_COLUMNS_SECTION_ID, column: 1 };
    const list = [
      field({ id: 'cust-pf-heading', width: 'half', style: 'heading', value: 'Bill to', ...left }),
      field({ id: 'cust-pf-name', width: 'half', value: '{{customer.name}}', ...left }),
      field({ id: 'cust-pf-contact', width: 'half', value: '{{contact.name}}', ...left }),
      field({ id: 'cust-pf-address', width: 'half', value: '{{customer.address}}', ...left }),
      field({ id: 'cust-pb-heading', width: 'half', align: 'right', style: 'heading', value: 'Job site', ...right }),
      field({ id: 'cust-pb-name', width: 'half', align: 'right', name: 'Job site name', value: '{{jobsite.name}}', ...right }),
      field({ id: 'cust-pb-address', width: 'half', align: 'right', name: 'Job site address', value: '{{jobsite.address}}', ...right }),
      field({ id: 'cust-pb-contact', width: 'half', align: 'right', name: 'Job site contact', value: '{{jobsite.contact_name}}', ...right })
    ];
    return list.map((f, idx) => ({ ...f, sortOrder: idx }));
  })(),
  // Figma `be.signature.editor`: a statement Text above two rows of two signature lines. The lines sit in
  // a 2-column section so a line can move to the other column (LLAMS-699); the statement has a section of its own.
  signature: (() => {
    const lines: Array<[string, string]> = [
      ['Customer signature', 'Printed name & date'],
      ['Contractor signature', 'Printed name & date']
    ];
    const list = [
      field({
        id: 'sig-statement',
        style: 'rich_text',
        label: 'Statement',
        name: 'Text',
        value: '<p>By signing below, the customer accepts this quote and its terms, and this proposal becomes a binding agreement.</p>',
        sectionId: 'sec-signature-statement',
        column: 0
      }),
      ...lines.flatMap(([left, right], row) => [
        field({
          id: `sig-line-${row * 2 + 1}`,
          width: 'half',
          style: 'signature_line',
          label: left,
          name: `Signature line ${row * 2 + 1}`,
          sectionId: 'sec-signature-lines',
          column: 0
        }),
        field({
          id: `sig-line-${row * 2 + 2}`,
          width: 'half',
          style: 'signature_line',
          label: right,
          name: `Signature line ${row * 2 + 2}`,
          sectionId: 'sec-signature-lines',
          column: 1
        })
      ])
    ];
    return list.map((f, idx) => ({ ...f, sortOrder: idx }));
  })()
};

const registry = new Map<string, FieldDefaultsForDocType>();

// Register the canonical fallback document type for quote tokens.
registry.set('quote', QUOTE_DEFAULTS);

export function registerDefaultFieldsForDocumentType(documentTypeKey: string, defaults: FieldDefaultsForDocType) {
  const existing = registry.get(documentTypeKey);
  const next: FieldDefaultsForDocType = { ...(existing ?? {}) };

  // Only override provided kinds (so registering one key doesn't replace the rest).
  for (const [kind, value] of Object.entries(defaults) as Array<[FieldKind, BlockField[]]>) {
    next[kind] = value;
  }

  registry.set(documentTypeKey, next);
}

export function getDefaultFieldsForDocumentType(documentTypeKey?: string | null): Record<FieldKind, BlockField[]> {
  const resolvedKey = documentTypeKey ?? null;
  const registered = resolvedKey ? registry.get(resolvedKey) : undefined;

  // Merge with quote fallback per-kind.
  const header = (registered?.header ?? QUOTE_DEFAULTS.header) as BlockField[];
  const customer_block = (registered?.customer_block ?? QUOTE_DEFAULTS.customer_block) as BlockField[];
  const signature = (registered?.signature ?? QUOTE_DEFAULTS.signature) as BlockField[];

  return { header, customer_block, signature };
}
